// @ts-check
import React, { useEffect, useMemo, useState } from 'react'
import { InputText } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import {
  LATIN_LETTERS_WITH_SYMBOLS, LATIN_WITH_NUMBERS_ADDRESS_SYMBOLS,
  LATIN_WITH_NUMBERS_SYMBOLS,
  MAX_LENGTH,
  MAX_LENGTH_CITY, NOT_ALLOWED_ADDRESS_SYMBOLS, STATE_CHECK_REGEX,
  VALIDATE_NAME
} from '../../../constants/constants'
import { isNil, map } from 'ramda'
import { getUserValue } from '../utils'

/**
 * @param {{
 * i18nLabel: string,
 * type: string,
 * name: string,
 * maxlength?: number,
 * additionalInfo?: string,
 * required: boolean,
 * order?: number,
 * allowNonLatin: boolean
 * }} props
 */
const ExternalTextField = ({ i18nLabel, type, name, maxlength, additionalInfo, required, allowNonLatin }) => {
  const { t } = useTranslation()
  const label = i18nLabel ? t(`generatedField.${i18nLabel}`) : ''
  const [value, setValue] = useState('')
  const [isValid, setIsValid] = useState(null)
  const [isFilled, setIsFilled] = useState(false)
  const [error, setError] = useState('')
  const [{ user, form, form: { accId } }, dispatch] = useStore()
  const userValue = getUserValue(accId, user, name)
  const { residenceCountry } = form[accId] || {}

  const presetValue = (value) => {
    setValue(value)
    const isValid = getIsValid(name, value, allowNonLatin)
    setIsValid(isValid)
    const filled = !!value.trim()
    setIsFilled(filled)
    setError(!filled ? '' : getErrorMsg(name, value, allowNonLatin))
    dispatch({ type: 'setExtFieldValue', name, value })
    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  const placeholder = useMemo(() => {
    switch (name) {
      case 'firstName':
        return t('placeholders.firstName')
      case 'lastName':
        return t('placeholders.lastName')
      case 'postalCode':
        if (residenceCountry === 'GBR') {
          return 'AA9A 9AA'
        }
        return undefined
      default:
        return undefined
    }
  }, [t, name, residenceCountry])

  useEffect(() => {
    if (!isNil(userValue) && userValue !== '') {
      presetValue(userValue)
    }
  }, [userValue, name])
  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  const onChange = ({ target: { value } }) => {
    setValue(value)
    !value.trim() ? setIsFilled(false) : setIsFilled(true)
  }
  /**
   * @param {React.FocusEvent<HTMLInputElement>} e
   */
  const onBlur = ({ target: { name, value } }) => {
    const trimmedValue = value.trim()
    let isValid
    if (!required) {
      isValid = !trimmedValue.length || (trimmedValue.length && getIsValid(name, trimmedValue, allowNonLatin))
    } else {
      isValid = getIsValid(name, trimmedValue, allowNonLatin)
    }

    if (isValid) {
      setIsValid(true)
      setError('')
      dispatch({ type: 'setExtFieldValue', name, value })
    } else {
      setIsValid(false)
      setError(!isFilled ? '' : getErrorMsg(name, value, allowNonLatin))
    }

    dispatch({ type: 'setValidationFld', name, value: isValid })
  }

  /**
   * @param {string} name
   * @param {string} value
   * @param {boolean} allowNonLatin
   * @returns {boolean}
   */
  const getIsValid = (name, value, allowNonLatin) => {
    switch (name) {
      case 'firstName':
      case 'lastName':
        return VALIDATE_NAME.test(value)
      case 'taxIdNumber':
      case 'bankAccountNumber':
      case 'branchName':
      case 'fundName':
        return allowNonLatin ? !!value.length : LATIN_WITH_NUMBERS_SYMBOLS.test(value)
      case 'sourceOfWealth':
        return allowNonLatin ? !!value.length : /^[A-Za-z\s]+$/.test(value)
      case 'bankName':
      case 'bankAccountName':
        return allowNonLatin ? !!value.length : LATIN_LETTERS_WITH_SYMBOLS.test(value)
      case 'city':
      case 'state':
        return !NOT_ALLOWED_ADDRESS_SYMBOLS.test(value) && (allowNonLatin ? !!value.length : STATE_CHECK_REGEX.test(value))
      case 'beneficiaryAddress':
      case 'address':
      case 'postalCode':
        return !NOT_ALLOWED_ADDRESS_SYMBOLS.test(value) && (allowNonLatin
          ? !!value.length
          : LATIN_WITH_NUMBERS_ADDRESS_SYMBOLS.test(value))
      default:
        return !!value.length
    }
  }

  /**
   * @param {string} name
   * @param {string} value
   * @param {boolean} allowNonLatin
   * @returns {string}
   */
  const getErrorMsg = (name, value, allowNonLatin) => {
    switch (name) {
      case 'address':
      case 'city':
      case 'state':
      case 'beneficiaryAddress':
      case 'postalCode':
        return getIsValid(name, value, allowNonLatin)
          ? ''
          : allowNonLatin
            ? 'generatedField.notAllowedSpecialSymbols'
            : 'generatedField.onlyLatin'
      case 'firstName':
      case 'lastName':
      case 'taxIdNumber':
      case 'sourceOfWealth':
      case 'bankName':
      case 'bankAccountName':
      case 'bankAccountNumber':
      case 'branchName':
      case 'fundName':
        return getIsValid(name, value, allowNonLatin) ? '' : 'generatedField.onlyLatin'
      default:
        return ''
    }
  }

  /**
   *
   * @param name
   * @returns {number}
   */
  const getMaxLength = (name) => {
    switch (name) {
      case 'postalCode':
      case 'taxIdNumber':
        return 100
      case 'state':
      case 'firstName':
      case 'lastName':
      case 'bankName':
      case 'bankAccountName':
      case 'bankAccountNumber':
        return 200
      case 'city':
        return MAX_LENGTH_CITY
      default:
        return MAX_LENGTH
    }
  }

  return (
    <InputText
      name={name}
      label={label}
      value={value}
      type={type}
      key={name}
      maxLength={maxlength || getMaxLength(name)}
      error={t(error)}
      onChange={onChange}
      onBlur={onBlur}
      valid={isValid}
      info={additionalInfo ? t(`generatedField.${name}AdditionalInfo`) : ''}
      placeholder={placeholder}
    />
  )
}

const getExternalTextFields = map(textField => {
  const { i18n, name, required, type, order, maxLength, additionalInfo, allowNonLatin } = textField

  return (
    <ExternalTextField
      i18nLabel={i18n}
      name={name}
      required={required}
      type={type}
      key={name}
      order={order}
      additionalInfo={additionalInfo}
      maxlength={maxLength}
      allowNonLatin={allowNonLatin}
    />
  )
})

const getExternalUnknownFields = map(unknownField => {
  const { i18n, name, required, type, allowNonLatin } = unknownField

  return (
    <ExternalTextField i18nLabel={i18n} name={name} required={required} type={type} key={name} allowNonLatin={allowNonLatin} />
  )
})

export { ExternalTextField, getExternalTextFields, getExternalUnknownFields }
